import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { debounce } from 'lodash';
import {
  FaEye,
  FaTrashAlt,
  FaTags,
  FaSearch,
  FaFilter,
  FaSort,
} from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import Pagination from './Pagination';
import ConfirmationDialog from './ConfirmationDialog';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
  timeout: 5000,
});

const WelcomeTextMessageList = () => {
  const { restaurantId } = useParams();
  const navigate = useNavigate();
  const [welcomeTexts, setWelcomeTexts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({});
  const [filters, setFilters] = useState({
    currentPage: 1,
    textsPerPage: 10,
    searchTerm: '',
    statusFilter: 'all',
    messageTypeFilter: 'all',

    dateFilter: { start: null, end: null },
    sortField: 'createdAt',
    sortDirection: 'desc',
  });

  const [selectedTexts, setSelectedTexts] = useState([]);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [deleteAction, setDeleteAction] = useState(null);

  const fetchWelcomeTexts = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('ownerToken');
      const response = await api.get(
        `/restaurants/${restaurantId}/welcome-texts`,
        {
          params: {
            page: filters.currentPage,
            limit: filters.textsPerPage,
            sortBy: filters.sortField,
            sortOrder: filters.sortDirection,
            status: filters.statusFilter,
            messageType:
              filters.messageTypeFilter !== 'all'
                ? filters.messageTypeFilter
                : undefined,
            search: filters.searchTerm,
            startDate: filters.dateFilter.start,
            endDate: filters.dateFilter.end,
          },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      console.log(response.data);
      setWelcomeTexts(response.data.welcomeTexts);
      setPagination({
        currentPage: response.data.currentPage,
        totalPages: response.data.totalPages,
        totalTexts: response.data.totalTexts,
      });
      setError(null);
    } catch (err) {
      setError('Failed to fetch welcome texts. Please try again.');
      toast.error('Failed to fetch welcome texts. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [restaurantId, filters]);

  useEffect(() => {
    fetchWelcomeTexts();
  }, [fetchWelcomeTexts]);

  const handleSoftDelete = useCallback(
    async (textId) => {
      try {
        const token = localStorage.getItem('ownerToken');
        await api.delete(
          `/restaurants/${restaurantId}/welcome-texts/${textId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        fetchWelcomeTexts();
        toast.success('Welcome text deleted successfully');
      } catch (error) {
        console.error('Error soft deleting welcome text', error);
        toast.error('Failed to delete welcome text. Please try again.');
      }
    },
    [restaurantId, fetchWelcomeTexts]
  );

  const handleSearchChange = debounce((value) => {
    setFilters((prev) => ({ ...prev, searchTerm: value, currentPage: 1 }));
  }, 300);

  const handleFilterChange = (filterName, value) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [filterName]: value,
      currentPage: filterName === 'currentPage' ? value : 1,
    }));
  };

  const resetFilters = () => {
    setFilters({
      currentPage: 1,
      textsPerPage: 10,
      searchTerm: '',
      statusFilter: 'all',
      dateFilter: { start: null, end: null },
      sortField: 'createdAt',
      sortDirection: 'desc',
      messageTypeFilter: 'all',
    });
  };

  const handlePageChange = (newPage) => {
    setFilters((prev) => ({ ...prev, currentPage: newPage }));
  };

  const handleSelectText = (textId) => {
    setSelectedTexts((prev) =>
      prev.includes(textId)
        ? prev.filter((id) => id !== textId)
        : [...prev, textId]
    );
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedTexts(memoizedWelcomeTexts.map((text) => text._id));
    } else {
      setSelectedTexts([]);
    }
  };

  const handleDeleteClick = (textId) => {
    setDeleteAction({ type: 'single', id: textId });
    setShowConfirmDialog(true);
  };

  const handleDeleteSelectedClick = () => {
    if (selectedTexts.length > 0) {
      setDeleteAction({ type: 'multiple', ids: selectedTexts });
      setShowConfirmDialog(true);
    } else {
      toast.warn('No texts selected');
    }
  };

  const handleConfirmDelete = async () => {
    try {
      const token = localStorage.getItem('ownerToken');
      if (deleteAction.type === 'single') {
        console.log(deleteAction.id);
        await api.delete(
          `/restaurants/${restaurantId}/welcome-texts/${deleteAction.id}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        toast.success('Welcome text deleted successfully');
      } else if (deleteAction.type === 'multiple') {
        await api.post(
          `/restaurants/${restaurantId}/welcome-texts/delete-multiple`,
          {
            textIds: deleteAction.ids,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        toast.success('Selected welcome texts deleted successfully');
      }
      fetchWelcomeTexts();
      setSelectedTexts([]);
    } catch (error) {
      toast.error('Failed to delete welcome text(s)');
    } finally {
      setShowConfirmDialog(false);
      setDeleteAction(null);
    }
  };

  const memoizedWelcomeTexts = useMemo(() => welcomeTexts, [welcomeTexts]);

  return (
    <div className='container mx-auto px-4 py-8'>
      <div className='flex justify-between items-center mb-6'>
        <h1 className='text-3xl font-bold'>Sent Welcome Text Messages</h1>
        <button
          className='bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-800 transition duration-300 ease-in-out shadow-md flex items-center'
          onClick={() => navigate(`/restaurant/${restaurantId}/welcometext`)}
        >
          Edit Welcome Text Template
        </button>
      </div>

      {/* Search and filter components */}
      <div className='mb-4 relative'>
        <input
          type='text'
          placeholder='Search welcome texts OR phone number...'
          className='w-full pl-12 pr-4 py-2 border rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
          onChange={(e) => handleSearchChange(e.target.value)}
        />
        <FaSearch className='absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400' />
      </div>

      <div className='mb-4 flex flex-wrap items-center gap-4'>
        {/* Status filter */}
        <div className='flex items-center'>
          <FaFilter className='text-gray-600' />
          <select
            className='ml-2 p-2 border rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
            onChange={(e) => handleFilterChange('statusFilter', e.target.value)}
            value={filters.statusFilter}
          >
            <option value='all'>All Statuses</option>
            <option value='sent'>Sent</option>
            <option value='failed'>Failed</option>
          </select>
        </div>

        <div className='flex items-center'>
          <FaFilter className='text-gray-600' />
          <select
            className='ml-2 p-2 border rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
            onChange={(e) =>
              handleFilterChange('messageTypeFilter', e.target.value)
            }
            value={filters.messageTypeFilter}
          >
            <option value='all'>All Types</option>
            <option value='text'>Text</option>
            <option value='coupon'>Coupon</option>
          </select>
        </div>

        {/* Date filters */}
        <div className='flex items-center'>
          <label className='whitespace-nowrap mr-2'>Sent (From):</label>
          <input
            type='date'
            className='p-2 border rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
            onChange={(e) =>
              handleFilterChange('dateFilter', {
                ...filters.dateFilter,
                start: e.target.value,
              })
            }
            value={filters.dateFilter.start || ''}
          />
        </div>

        <div className='flex items-center'>
          <label className='whitespace-nowrap mr-2'>Sent (To):</label>
          <input
            type='date'
            className='p-2 border rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
            onChange={(e) =>
              handleFilterChange('dateFilter', {
                ...filters.dateFilter,
                end: e.target.value,
              })
            }
            value={filters.dateFilter.end || ''}
          />
        </div>

        {/* Sort options */}
        <div className='flex items-center'>
          <FaSort className='text-gray-600' />
          <select
            className='ml-2 p-2 border rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
            onChange={(e) => handleFilterChange('sortField', e.target.value)}
            value={filters.sortField}
          >
            <option value='createdAt'>Sort by Sent Date</option>
            <option value='status'>Sort by Status</option>
          </select>
          <button
            className='ml-2 p-2 border rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
            onClick={() =>
              handleFilterChange(
                'sortDirection',
                filters.sortDirection === 'asc' ? 'desc' : 'asc'
              )
            }
          >
            {filters.sortDirection === 'asc' ? '↑' : '↓'}
          </button>
        </div>

        <button
          onClick={resetFilters}
          className='bg-red-500 text-white px-6 py-2 rounded hover:bg-red-700 transition duration-300 ease-in-out shadow-md'
        >
          Clear All Filters
        </button>
      </div>

      <div className='mb-4 flex justify-between items-center'>
        <div>
          <label className='inline-flex items-center'>
            <input
              type='checkbox'
              checked={selectedTexts.length === memoizedWelcomeTexts.length}
              onChange={handleSelectAll}
              className='mr-2'
            />
            <span>Select All</span>
          </label>
        </div>
        <div>
          <span className='mr-4'>
            Selected: {selectedTexts.length} out of{' '}
            {memoizedWelcomeTexts.length}
          </span>
          <button
            onClick={handleDeleteSelectedClick}
            disabled={selectedTexts.length === 0}
            className='bg-red-500 text-white px-4 py-2 rounded disabled:opacity-50'
          >
            Delete Selected
          </button>
        </div>
      </div>

      {/* Welcome Text Messages list */}
      {error ? (
        <div className='text-red-500 text-xl text-center'>{error}</div>
      ) : loading ? (
        <div className='flex justify-center items-center'>
          <div className='animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-blue-500'></div>
        </div>
      ) : (
        <div className='mt-6'>
          {/* {memoizedWelcomeTexts.length > 0 ? (
            <div className='grid grid-cols-1 gap-4'>
              {memoizedWelcomeTexts.map((text) => (
                <WelcomeTextCard
                  key={text._id}
                  text={text}
                  onDelete={handleSoftDelete}
                  restaurantId={restaurantId}
                />
              ))}
            </div>
          ) : (
            <p className='text-center'>No welcome text messages found.</p>
          )} */}
          {memoizedWelcomeTexts.length > 0 ? (
            <div className='grid grid-cols-1 gap-4'>
              {memoizedWelcomeTexts.map((text) => (
                <WelcomeTextCard
                  key={text._id}
                  text={text}
                  onDelete={handleDeleteClick}
                  restaurantId={restaurantId}
                  isSelected={selectedTexts.includes(text._id)}
                  onSelect={() => handleSelectText(text._id)}
                />
              ))}
            </div>
          ) : (
            <p className='text-center'>No welcome text messages found.</p>
          )}
        </div>
      )}

      {/* Pagination */}
      {/* <div className='flex justify-center mt-5'>
        {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map(
          (number) => (
            <button
              key={number}
              onClick={() => handleFilterChange('currentPage', number)}
              className={`mx-1 px-3 py-1 rounded ${
                pagination.currentPage === number
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-200 hover:bg-gray-300'
              }`}
            >
              {number}
            </button>
          )
        )}
      </div>

      <div className='flex justify-between items-center mt-4'>
        <div>
          <span className='mr-2'>Texts per page:</span>
          <select
            value={filters.textsPerPage}
            onChange={(e) =>
              handleFilterChange('textsPerPage', Number(e.target.value))
            }
            className='p-2 border rounded'
          >
            <option value='5'>5</option>
            <option value='10'>10</option>
            <option value='20'>20</option>
            <option value='50'>50</option>
          </select>
        </div>

        <div>
          <span>
            Showing {(pagination.currentPage - 1) * filters.textsPerPage + 1} -{' '}
            {Math.min(
              pagination.currentPage * filters.textsPerPage,
              pagination.totalTexts
            )}{' '}
            of {pagination.totalTexts}
          </span>
        </div>
      </div> */}
      {!loading && !error && (
        <Pagination
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          onPageChange={handlePageChange}
          itemsPerPage={filters.textsPerPage}
          totalItems={pagination.totalCoupons}
        />
      )}

      <div className='flex justify-between items-center mt-4'>
        <div>
          <span className='mr-2'># per page:</span>
          <select
            value={filters.textsPerPage}
            onChange={(e) =>
              handleFilterChange('messagesPerPage', Number(e.target.value))
            }
            className='p-2 border rounded'
          >
            {/* <option value='1'>1</option> */}
            <option value='5'>5</option>
            <option value='10'>10</option>
            <option value='20'>20</option>
            <option value='50'>50</option>
          </select>
        </div>

        <div>
          <span>
            Showing {(pagination.currentPage - 1) * filters.textsPerPage + 1} -{' '}
            {Math.min(
              pagination.currentPage * filters.textsPerPage,
              pagination.totalTexts
            )}{' '}
            of {pagination.totalMessages}
            {/* {pagination.totalCoupons} */}
          </span>
        </div>
      </div>

      <ConfirmationDialog
        isOpen={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        onConfirm={handleConfirmDelete}
        title='Confirm Deletion'
        message={
          deleteAction?.type === 'single'
            ? 'Are you sure you want to delete this welcome text? This action cannot be undone.'
            : `Are you sure you want to delete ${deleteAction?.ids.length} selected welcome texts? This action cannot be undone.`
        }
      />
    </div>
  );
};

// const WelcomeTextCard = React.memo(({ text, onDelete, restaurantId }) => (
//   <div className='bg-white shadow-lg p-4 rounded relative'>
//     <p className='text-sm font-semibold text-gray-600'>{text.content}</p>
//     <p className='text-sm text-gray-500'>
//       Sent: {new Date(text.createdAt).toLocaleString()}
//     </p>
//     <p className='text-sm text-gray-500'>Status: {text.status}</p>
//     <p className='text-sm text-gray-500'>Message Type: {text.messageType}</p>
//     <p className='text-sm text-gray-500'>
//       Sent to: {text.customer ? text.customer.phoneNumber : 'Unknown'}
//     </p>
//     {text.imageUrl && (
//       <p className='text-sm text-gray-500'>
//         Image:{' '}
//         <a
//           href={text.imageUrl}
//           target='_blank'
//           rel='noopener noreferrer'
//           className='hover:text-blue-600'
//         >
//           View Image
//         </a>
//       </p>
//     )}
//     <div className='mt-4 flex flex-wrap gap-2'>
//       <button
//         onClick={() => onDelete(text._id)}
//         className='bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded'
//       >
//         <FaTrashAlt className='inline-block mr-2' />
//         Delete
//       </button>
//       {text.messageType === 'coupon' && text.coupon && (
//         <Link
//           to={`/restaurant/${restaurantId}/welcome-texts/${text._id}/coupon`}
//           className='bg-indigo-500 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded'
//         >
//           <FaTags className='inline-block mr-2' />
//           View Coupon
//         </Link>
//       )}
//     </div>
//   </div>
// ));

// const WelcomeTextCard = React.memo(({ text, onDelete, restaurantId }) => (
//   <div className='bg-white shadow-lg p-4 rounded relative'>

const WelcomeTextCard = React.memo(
  ({ text, onDelete, restaurantId, isSelected, onSelect }) => (
    <div
      className={`bg-white shadow-lg p-4 rounded relative ${
        isSelected ? 'border-2 border-blue-500' : ''
      } cursor-pointer hover:shadow-xl transition-shadow duration-300`}
      onClick={(e) => {
        if (
          e.target.type !== 'checkbox' &&
          e.target.tagName !== 'BUTTON' &&
          e.target.tagName !== 'A'
        ) {
          onSelect();
        }
      }}
    >
      <input
        type='checkbox'
        checked={isSelected}
        onChange={(e) => {
          e.stopPropagation();
          onSelect();
        }}
        className='absolute top-2 right-2'
      />

      <p className='text-sm font-semibold text-gray-600'>{text.content}</p>
      <p className='text-sm text-gray-500'>
        Sent: {new Date(text.createdAt).toLocaleString()}
      </p>
      <p className='text-sm text-gray-500'>Status: {text.status}</p>
      <p className='text-sm text-gray-500'>Message Type: {text.messageType}</p>
      <p className='text-sm text-gray-500'>
        Sent to: {text.customer ? text.customer.phoneNumber : 'Unknown'}
      </p>
      {text.imageUrl && (
        <p className='text-sm text-gray-500'>
          Image:{' '}
          <a
            href={text.imageUrl}
            target='_blank'
            rel='noopener noreferrer'
            className='hover:text-blue-600'
          >
            View Image
          </a>
        </p>
      )}
      {text.messageType === 'coupon' && text.coupon && (
        <>
          <p className='text-sm text-gray-500'>
            Coupon Code: {text.coupon.code}
          </p>
          <p className='text-sm text-gray-500'>
            Coupon Status:{' '}
            {text.coupon.isRedeemed ? 'Redeemed' : 'Not Redeemed'}
          </p>
        </>
      )}
      <div className='mt-4 flex flex-wrap gap-2'>
        {text.messageType === 'coupon' && text.coupon && (
          <a
            href={`http://localhost:3000/redeem/${text.coupon.code}`}
            target='_blank'
            rel='noopener noreferrer'
            className='bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded'
            onClick={(e) => e.stopPropagation()}
          >
            <FaEye className='inline-block mr-2' />
            View Coupon
          </a>
        )}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete(text._id);
          }}
          className='bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded'
        >
          <FaTrashAlt className='text-center' />
          {/* Delete */}
        </button>
      </div>
    </div>
  )
);
export default WelcomeTextMessageList;

// import React, { useState, useEffect, useCallback, useMemo } from 'react';
// import { useParams, useNavigate, Link } from 'react-router-dom';
// import { debounce } from 'lodash';
// import {
//   FaEye,
//   FaEdit,
//   FaTrashAlt,
//   FaTags,
//   FaSearch,
//   FaFilter,
//   FaSort,
// } from 'react-icons/fa';
// import { ToastContainer, toast } from 'react-toastify';
// import 'react-toastify/dist/ReactToastify.css';
// import axios from 'axios';

// const api = axios.create({
//   baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
//   timeout: 5000,
// });

// const WelcomeTextMessageList = () => {
//   const { restaurantId } = useParams();
//   const navigate = useNavigate();
//   const [welcomeTexts, setWelcomeTexts] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [pagination, setPagination] = useState({});
//   const [filters, setFilters] = useState({
//     currentPage: 1,
//     textsPerPage: 10,
//     searchTerm: '',
//     statusFilter: 'all',
//     dateFilter: { start: null, end: null },
//     sortField: 'createdAt',
//     sortDirection: 'desc',
//   });

//   const fetchWelcomeTexts = useCallback(async () => {
//     try {
//       setLoading(true);
//       const token = localStorage.getItem('ownerToken');
//       //   const response = await api.get(
//       //     `/restaurants/${restaurantId}/welcome-texts`,
//       //     {
//       const response = await api.get(
//         `/restaurants/${restaurantId}/welcome-texts`,
//         {
//           params: {
//             page: filters.currentPage,
//             limit: filters.textsPerPage,
//             sortBy: filters.sortField,
//             sortOrder: filters.sortDirection,
//             status: filters.statusFilter,
//             search: filters.searchTerm,
//             startDate: filters.dateFilter.start,
//             endDate: filters.dateFilter.end,
//           },
//           headers: { Authorization: `Bearer ${token}` },
//         }
//       );
//       console.log(response.data);
//       setWelcomeTexts(response.data.welcomeTexts);
//       setPagination({
//         currentPage: response.data.currentPage,
//         totalPages: response.data.totalPages,
//         totalTexts: response.data.totalTexts,
//       });
//       setError(null);
//     } catch (err) {
//       setError('Failed to fetch welcome texts. Please try again.');
//       toast.error('Failed to fetch welcome texts. Please try again.');
//     } finally {
//       setLoading(false);
//     }
//   }, [restaurantId, filters]);

//   useEffect(() => {
//     fetchWelcomeTexts();
//   }, [fetchWelcomeTexts]);

//   const handleSoftDelete = useCallback(
//     async (textId) => {
//       try {
//         const token = localStorage.getItem('ownerToken');
//         await api.delete(
//           `/restaurants/${restaurantId}/welcome-texts/${textId}`,
//           {
//             headers: { Authorization: `Bearer ${token}` },
//           }
//         );
//         fetchWelcomeTexts();
//         toast.success('Welcome text deleted successfully');
//       } catch (error) {
//         console.error('Error soft deleting welcome text', error);
//         toast.error('Failed to delete welcome text. Please try again.');
//       }
//     },
//     [restaurantId, fetchWelcomeTexts]
//   );

//   const handleSearchChange = debounce((value) => {
//     setFilters((prev) => ({ ...prev, searchTerm: value, currentPage: 1 }));
//   }, 300);

//   const handleFilterChange = (filterName, value) => {
//     setFilters((prevFilters) => ({
//       ...prevFilters,
//       [filterName]: value,
//       currentPage: filterName === 'currentPage' ? value : 1,
//     }));
//   };

//   const resetFilters = () => {
//     setFilters({
//       currentPage: 1,
//       textsPerPage: 10,
//       searchTerm: '',
//       statusFilter: 'all',
//       dateFilter: { start: null, end: null },
//       sortField: 'createdAt',
//       sortDirection: 'desc',
//     });
//   };

//   const memoizedWelcomeTexts = useMemo(() => welcomeTexts, [welcomeTexts]);

//   return (
//     <div className='container mx-auto px-4 py-8'>
//       <div className='flex justify-between items-center mb-6'>
//         <h1 className='text-3xl font-bold'>Sent Welcome Text Messages</h1>
//         <button
//           className='bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-800 transition duration-300 ease-in-out shadow-md flex items-center'
//           onClick={() => navigate(`/restaurant/${restaurantId}/welcometext`)}
//         >
//           Edit Welcome Text
//         </button>
//       </div>

//       {/* Search and filter components */}
//       <div className='mb-4 relative'>
//         <input
//           type='text'
//           placeholder='Search welcome texts...'
//           className='w-full pl-12 pr-4 py-2 border rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
//           onChange={(e) => handleSearchChange(e.target.value)}
//         />
//         <FaSearch className='absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400' />
//       </div>

//       <div className='mb-4 flex flex-wrap items-center gap-4'>
//         {/* Status filter */}
//         <div className='flex items-center'>
//           <FaFilter className='text-gray-600' />
//           <select
//             className='ml-2 p-2 border rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
//             onChange={(e) => handleFilterChange('statusFilter', e.target.value)}
//             value={filters.statusFilter}
//           >
//            <option value='all'>All Statuses</option>
//             <option value='sent'>Sent</option>
//             <option value='failed'>Failed</option>
//           </select>
//         </div>

//         {/* Date filters */}
//         <div className='flex items-center'>
//           <label className='whitespace-nowrap mr-2'>From:</label>
//           <input
//             type='date'
//             className='p-2 border rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
//             onChange={(e) =>
//               handleFilterChange('dateFilter', {
//                 ...filters.dateFilter,
//                 start: e.target.value,
//               })
//             }
//             value={filters.dateFilter.start || ''}
//           />
//         </div>

//         <div className='flex items-center'>
//           <label className='whitespace-nowrap mr-2'>To:</label>
//           <input
//             type='date'
//             className='p-2 border rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
//             onChange={(e) =>
//               handleFilterChange('dateFilter', {
//                 ...filters.dateFilter,
//                 end: e.target.value,
//               })
//             }
//             value={filters.dateFilter.end || ''}
//           />
//         </div>

//         {/* Sort options */}
//         <div className='flex items-center'>
//           <FaSort className='text-gray-600' />
//           <select
//             className='ml-2 p-2 border rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
//             onChange={(e) => handleFilterChange('sortField', e.target.value)}
//             value={filters.sortField}
//           >
//             <option value='createdAt'>Sort by Sent Date</option>
//             <option value='status'>Sort by Status</option>
//           </select>
//           <button
//             className='ml-2 p-2 border rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
//             onClick={() =>
//               handleFilterChange(
//                 'sortDirection',
//                 filters.sortDirection === 'asc' ? 'desc' : 'asc'
//               )
//             }
//           >
//             {filters.sortDirection === 'asc' ? '↑' : '↓'}
//           </button>
//         </div>

//         <button
//           onClick={resetFilters}
//           className='bg-red-500 text-white px-6 py-2 rounded hover:bg-red-700 transition duration-300 ease-in-out shadow-md'
//         >
//           Clear All Filters
//         </button>
//       </div>

//       {/* Welcome Text Messages list */}
//       {error ? (
//         <div className='text-red-500 text-xl text-center'>{error}</div>
//       ) : loading ? (
//         <div className='flex justify-center items-center'>
//           <div className='animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-blue-500'></div>
//         </div>
//       ) : (
//         <div className='mt-6'>
//           {memoizedWelcomeTexts.length > 0 ? (
//             <div className='grid grid-cols-1 gap-4'>
//               {memoizedWelcomeTexts.map((text) => (
//                 <WelcomeTextCard
//                   key={text._id}
//                   text={text}
//                   onDelete={handleSoftDelete}
//                   restaurantId={restaurantId}
//                 />
//               ))}
//             </div>
//           ) : (
//             <p className='text-center'>No welcome text messages found.</p>
//           )}
//         </div>
//       )}

//       {/* Pagination */}
//       <div className='flex justify-center mt-5'>
//         {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map(
//           (number) => (
//             <button
//               key={number}
//               onClick={() => handleFilterChange('currentPage', number)}
//               className={`mx-1 px-3 py-1 rounded ${
//                 pagination.currentPage === number
//                   ? 'bg-blue-500 text-white'
//                   : 'bg-gray-200 hover:bg-gray-300'
//               }`}
//             >
//               {number}
//             </button>
//           )
//         )}
//       </div>

//       {/* Page size selector and info */}
//       <div className='flex justify-between items-center mt-4'>
//         <div>
//           <span className='mr-2'>Texts per page:</span>
//           <select
//             value={filters.textsPerPage}
//             onChange={(e) =>
//               handleFilterChange('textsPerPage', Number(e.target.value))
//             }
//             className='p-2 border rounded'
//           >
//             <option value='5'>5</option>
//             <option value='10'>10</option>
//             <option value='20'>20</option>
//             <option value='50'>50</option>
//           </select>
//         </div>

//         <div>
//           <span>
//             Showing {(pagination.currentPage - 1) * filters.textsPerPage + 1} -{' '}
//             {Math.min(
//               pagination.currentPage * filters.textsPerPage,
//               pagination.totalTexts
//             )}{' '}
//             of {pagination.totalTexts}
//           </span>
//         </div>
//       </div>
//     </div>
//   );
// };

// const WelcomeTextCard = React.memo(({ text, onDelete, restaurantId }) => (
//   <div className='bg-white shadow-lg p-4 rounded relative'>
//     <h2 className='text-lg font-semibold'>{text.title}</h2>
//     <p className='text-sm font-semibold text-gray-600'>{text.content}</p>
//     <p className='text-sm text-gray-500'>
//       Created: {new Date(text.createdAt).toLocaleString()}
//     </p>
//     {/* <p className='text-sm text-gray-500'>
//       {text.isActive ? 'Active' : 'Inactive'}
//     </p> */}
//     <p className='text-sm text-gray-500'>Status: {text.status}</p>
//     <p className='text-sm text-gray-500'>Message Type: {text.messageType}</p>
//     {text.imageUrl && (
//       <p className='text-sm text-gray-500'>
//         Image:{' '}
//         <a
//           href={text.imageUrl}
//           target='_blank'
//           rel='noopener noreferrer'
//           className='hover:text-blue-600'
//         >
//           {text.imageUrl}
//         </a>
//       </p>
//     )}
//     <div className='mt-4 flex flex-wrap gap-2'>
//       {/* <Link
//         to={`/restaurant/${restaurantId}/welcome-texts/${text._id}/view`}
//         className='bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded'
//       >
//         <FaEye className='inline-block mr-2' />
//         View
//       </Link> */}
//       {/* <Link
//         to={`/restaurant/${restaurantId}/welcome-texts/${text._id}/edit`}
//         className='bg-yellow-500 hover:bg-yellow-700 text-white font-bold py-2 px-4 rounded'
//       >
//         <FaEdit className='inline-block mr-2' />
//         Edit
//       </Link> */}
//       <button
//         onClick={() => onDelete(text._id)}
//         className='bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded'
//       >
//         <FaTrashAlt className='inline-block mr-2' />
//         Delete
//       </button>
//       {text.messageType === 'text_coupon' && (
//         <Link
//           to={`/restaurant/${restaurantId}/welcome-texts/${text._id}/coupons`}
//           className='bg-indigo-500 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded'
//         >
//           <FaTags className='inline-block mr-2' />
//           Coupons
//         </Link>
//       )}
//     </div>
//   </div>
// ));

// export default WelcomeTextMessageList;
